export const APP_NAME = 'iCPV';
export const APP_VERSION = '1.0.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };

import type { ChartImage, DropdownOption } from '../shared';
import type { ParsedValues } from './validation';

export type APIPayload = Omit<ParsedValues, 'project'>;

export type ChartKey = 'graph_1' | 'graph_2' | 'graph_3' | 'graph_4' | 'graph_5';

const SHAPE_OPTIONS: DropdownOption[] = [
  { value: 0, label: 'Spherical' },
  { value: 1, label: 'Cylindrical' }
];

const GROUND_OPTIONS: DropdownOption[] = [
  { value: 0, label: 'Elevated' },
  { value: 1, label: 'On the ground' }
];

const SPECIFIC_HEAT_OPTIONS: DropdownOption[] = [
  { value: 1.4, label: 'Ideal gas = 1.4' },
  { value: 1.667, label: 'Inert gas = 1.667' }
];

const FRAGMENT_OPTIONS: DropdownOption[] = [
  { value: 2, label: 'n = 2' },
  { value: 10, label: 'n = 10' },
  { value: 100, label: 'n = 100' }
];

const RUPTURE_OPTIONS: DropdownOption[] = [
  { value: 0, label: 'Circular Fragment' },
  { value: 1, label: 'Rectangular Fragment' }
];

export const DROPDOWN_OPTIONS = {
  iShape: SHAPE_OPTIONS,
  iGround: GROUND_OPTIONS,
  numberFragments: FRAGMENT_OPTIONS,
  ruptureType: RUPTURE_OPTIONS,
  specificHeat: SPECIFIC_HEAT_OPTIONS
};

export type APIResponse = {
  Es: number;
  Mg: number;
  Weq: number;
  R: number;
  Pi: number;
  Ii: number;
  Pr: number;
  Ir: number;
  W: number;
  Rw: number;
  nFrags: number;
  Vo: number;
  Wf1?: number;
  Wf2?: number;
  Vo1?: number;
  Vo2?: number;
  Vo1a?: number;
  Vo2a?: number;
  Vo1i?: number;
  Vo2i?: number;
  Wf?: number;
  V?: number;
  charts: Partial<Record<ChartKey, ChartImage>>;
};

import type { SectionConfig } from '../shared';
import { createElement as h } from 'react';

// ===== Helper Functions =====
const sub = (text: string, subscript: string): React.ReactNode => h('span', null, text, h('sub', null, subscript));

const fmt = (decimals: number, unit: string) => (val: number) => `${val.toFixed(decimals)}${unit ? ' ' + unit : ''}`;

// ===== Field Metadata =====
type FieldMeta = {
  label: React.ReactNode;
  decimals?: number;
  unit?: string;
  show?: (data: APIResponse) => boolean;
};

type DisplayField = {
  label: React.ReactNode;
  getValue: (data: APIResponse) => React.ReactNode;
  show?: (data: APIResponse) => boolean;
};

const FIELDS: Record<string, FieldMeta> = {
  Es: { label: sub('E', 's'), decimals: 2, unit: 'mJ' },
  Mg: { label: sub('M', 'gas'), decimals: 3, unit: 'lb' },
  Weq: { label: sub('W', 'eq'), decimals: 2, unit: 'lb' },
  R: { label: 'R', decimals: 2, unit: 'ft' },
  Pi: { label: sub('P', 'i'), decimals: 2, unit: 'psi' },
  Ii: { label: sub('I', 'i'), decimals: 2, unit: 'psi-msec' },
  Pr: { label: sub('P', 'r'), decimals: 2, unit: 'psi' },
  Ir: { label: sub('I', 'r'), decimals: 2, unit: 'psi-msec' },
  W: { label: 'W', decimals: 2, unit: 'lb' },
  Rw: { label: 'R', decimals: 2, unit: 'ft' },
  Wf1: { label: sub('W', 'f1'), decimals: 2, unit: 'lb', show: d => d.Wf1 != null },
  Wf2: { label: sub('W', 'f2'), decimals: 2, unit: 'lb', show: d => d.Wf2 != null },
  Wf: { label: sub('W', 'f'), decimals: 2, unit: 'lb', show: d => d.Wf1 === null },
  Vo: { label: 'V', decimals: 2, unit: 'ft/sec', show: d => d.Wf1 === null },
  Vo1: { label: sub('V', '1'), decimals: 2, unit: 'ft/sec', show: d => d.Vo1 != null },
  Vo2: { label: sub('V', '2'), decimals: 2, unit: 'ft/sec', show: d => d.Vo2 != null },
  Vo1a: { label: sub('V', '1'), decimals: 2, unit: 'ft/sec', show: d => d.Vo1a != null },
  Vo2a: { label: sub('V', '2'), decimals: 2, unit: 'ft/sec', show: d => d.Vo2a != null },
  Vo1i: { label: sub('V', '1'), decimals: 2, unit: 'ft/sec', show: d => d.Vo1i != null },
  Vo2i: { label: sub('V', '2'), decimals: 2, unit: 'ft/sec', show: d => d.Vo2i != null }
};

const buildField = (key: keyof APIResponse, meta: FieldMeta): DisplayField => ({
  label: meta.label,
  getValue: (data: APIResponse) => fmt(meta.decimals || 2, meta.unit || '')(data[key] as number),
  ...(meta.show ? { show: meta.show } : {})
});

// ===== Display Config =====
export const RESPONSE_DISPLAY_CONFIG: SectionConfig<APIResponse>[] = [
  {
    title: 'Results',
    fields: [buildField('Es', FIELDS.Es), buildField('Mg', FIELDS.Mg), buildField('Weq', FIELDS.Weq), { type: 'subheader', label: 'Air-Blast Load' }, buildField('R', FIELDS.R), buildField('Pi', FIELDS.Pi), buildField('Ii', FIELDS.Ii), buildField('Pr', FIELDS.Pr), buildField('Ir', FIELDS.Ir), { type: 'subheader', label: 'Back Calculated' }, buildField('W', FIELDS.W), buildField('Rw', FIELDS.Rw)]
  },
  {
    title: 'Fragments',
    fields: [
      { type: 'subheader', label: 'Fragments' },
      buildField('Wf1', FIELDS.Wf1),
      buildField('Wf2', FIELDS.Wf2),
      buildField('Wf', FIELDS.Wf),
      buildField('Vo', FIELDS.Vo),
      { type: 'subheader', label: 'Empirical' },
      buildField('Vo1', FIELDS.Vo1),
      buildField('Vo2', FIELDS.Vo2),
      { type: 'subheader', label: 'Adiabatic' },
      buildField('Vo1a', FIELDS.Vo1a),
      buildField('Vo2a', FIELDS.Vo2a),
      { type: 'subheader', label: 'Isothermal' },
      buildField('Vo1i', FIELDS.Vo1i),
      buildField('Vo2i', FIELDS.Vo2i)
    ]
  }
];

// ===== Print Config =====
export const PRINT_RESULTS_CONFIG: DisplayField[] = [
  buildField('Es', FIELDS.Es),
  buildField('Mg', FIELDS.Mg),
  buildField('Weq', FIELDS.Weq),
  buildField('R', FIELDS.R),
  buildField('Pi', FIELDS.Pi),
  buildField('Ii', FIELDS.Ii),
  buildField('Pr', FIELDS.Pr),
  buildField('Ir', FIELDS.Ir),
  buildField('W', FIELDS.W),
  buildField('Rw', FIELDS.Rw),
  buildField('Wf1', FIELDS.Wf1),
  buildField('Wf2', FIELDS.Wf2),
  buildField('Wf', FIELDS.Wf),
  buildField('Vo', FIELDS.Vo),
  buildField('Vo1', FIELDS.Vo1),
  buildField('Vo2', FIELDS.Vo2),
  buildField('Vo1a', FIELDS.Vo1a),
  buildField('Vo2a', FIELDS.Vo2a),
  buildField('Vo1i', FIELDS.Vo1i),
  buildField('Vo2i', FIELDS.Vo2i)
];
